import {PivotTimeframe} from "../types/pivotPoints.ts";

/**
 * Utility functions for calculating date ranges for pivot point and confluence zone analysis.
 *
 * Date Range Strategy:
 * - Fetch Range: Extended lookback period to provide sufficient historical data for pattern detection
 *   and to enable panning/zooming backward in visualizations
 * - Initial Visible Range: Shorter period for clean initial display while still having access to
 *   fetched historical data via panning
 */

export interface DateRange {
  start: Date;
  end: Date;
}

/**
 * Calculate extended date range for fetching pivot/confluence data.
 * Uses longer lookback periods to:
 * - Provide sufficient historical context for confluence zone detection
 * - Enable backward panning in visualizations
 * - Support statistical analysis across multiple pivot periods
 *
 * @param timeframe - The pivot timeframe to calculate range for
 * @param customStart - Optional custom start date (overrides calculation)
 * @param customEnd - Optional custom end date (overrides calculation)
 * @returns DateRange with UTC-aligned start and end dates
 */
export function getPivotFetchRange(
  timeframe: PivotTimeframe,
  customStart?: Date,
  customEnd?: Date
): DateRange {
  if (customStart && customEnd) {
    return { start: customStart, end: customEnd };
  }

  const nowUtc = Date.now();
  const end = new Date(nowUtc);
  const start = new Date(nowUtc);

  // Extended ranges for historical analysis and chart panning
  switch (timeframe) {
    case 'THIRTY_DAY':
      start.setUTCDate(start.getUTCDate() - 60); // Fetch 60 days
      break;
    case 'SEVEN_DAY':
      start.setUTCDate(start.getUTCDate() - 30); // Fetch 30 days
      break;
    case 'TWENTY_FOUR_HOUR':
      start.setUTCDate(start.getUTCDate() - 12); // Fetch 12 days
      break;
    case 'TWELVE_HOUR':
      start.setUTCDate(start.getUTCDate() - 6); // Fetch 6 days
      break;
    case 'EIGHT_HOUR':
      start.setUTCDate(start.getUTCDate() - 4); // Fetch 4 days
      break;
    case 'FOUR_HOUR':
      start.setUTCDate(start.getUTCDate() - 2); // Fetch 2 days
      break;
    case 'TWO_HOUR':
      start.setUTCHours(start.getUTCHours() - 24); // Fetch 24 hours
      break;
    case 'ONE_HOUR':
      start.setUTCHours(start.getUTCHours() - 24); // Fetch 24 hours
      break;
    case 'THIRTY_MIN':
      start.setUTCHours(start.getUTCHours() - 12); // Fetch 12 hours
      break;
    default:
      start.setUTCDate(start.getUTCDate() - 1);
  }

  return { start, end };
}

/**
 * Calculate initial visible range for chart display.
 * These are shorter than fetch ranges to provide a clean initial view
 * while still allowing panning backward through fetched data.
 *
 * This represents what should be displayed on initial load before any user interaction.
 *
 * @param timeframe - The pivot timeframe to calculate visible range for
 * @returns DateRange for initial chart display
 */
export function getInitialVisibleRange(timeframe: PivotTimeframe): DateRange {
  const nowUtc = Date.now();
  const end = new Date(nowUtc);
  const start = new Date(nowUtc);

  // Shorter ranges for clean initial display
  switch (timeframe) {
    case 'THIRTY_DAY':
      start.setUTCDate(start.getUTCDate() - 21); // Display ~21 days
      break;
    case 'SEVEN_DAY':
      start.setUTCDate(start.getUTCDate() - 14); // Display ~14 days
      break;
    case 'TWENTY_FOUR_HOUR':
      start.setUTCDate(start.getUTCDate() - 3); // Display ~3 days
      break;
    case 'TWELVE_HOUR':
      start.setUTCDate(start.getUTCDate() - 2); // Display ~2 days
      break;
    case 'EIGHT_HOUR':
      start.setUTCDate(start.getUTCDate() - 1); // Display ~1 day
      break;
    case 'FOUR_HOUR':
      start.setUTCHours(start.getUTCHours() - 8); // Display ~8 hours
      break;
    case 'TWO_HOUR':
      start.setUTCHours(start.getUTCHours() - 8); // Display ~8 hours
      break;
    case 'ONE_HOUR':
      start.setUTCHours(start.getUTCHours() - 4); // Display ~4 hours
      break;
    case 'THIRTY_MIN':
      start.setUTCHours(start.getUTCHours() - 2); // Display ~2 hours
      break;
    default:
      start.setUTCDate(start.getUTCDate() - 1);
  }

  return { start, end };
}



